/**
 * Domain hook for PROSERV-2561: reads third-party client-specific disclaimers
 * and error messages for the Identity Widget from the CMS (via the reusable
 * `useGetUIContent` utility), with automatic fallback to the current
 * hardcoded values in `content.json` / `KNOWN_ERROR_MESSAGES` when the CMS
 * is unavailable or the requested clientId is not published.
 *
 * The hook returns raw content strings — the `{REDIRECT_URI}` placeholder is
 * preserved and substituted at the call site (existing behavior).
 *
 * Story: PROSERV-2561
 */

import { useMemo } from 'react';

import { KNOWN_ERROR_MESSAGES } from '../../api/constants';
import content from '../../content.json';
import { ClientId } from '../../utils/constants';

import { UI_CONTENT_IDS, useGetUIContent } from './useGetUIContent';

/**
 * Per-client integration content published in CMS under `ui_identity` ->
 * `client_integrations` (snake_case array format). All fields are optional
 * so partial CMS payloads gracefully degrade.
 */
export interface ClientIntegrationEntry {
  client_id?: string;
  client_name?: string;
  active?: boolean;
  disclaimer?: {
    terms_and_conditions?: string;
  };
  errors?: {
    authentication_failed?: string;
    locked_out?: string;
  };
}

/** Root shape of the `ui_identity` CMS payload. */
export interface UIIdentityContent {
  client_integrations?: ClientIntegrationEntry[];
}

/** Resolved content returned by `useClientIntegrationContent`. */
export interface ClientIntegrationContent {
  /** Sign-in disclaimer, or empty string if none configured. */
  disclaimer: string;
  /** Auth-failure error message (may include `{REDIRECT_URI}` placeholder). */
  authFailedError: string;
  /** Locked-out error message (may include `{REDIRECT_URI}` placeholder). */
  lockedOutError: string;
  /** True while the CMS request is in flight. */
  loading: boolean;
  /** Source of the resolved content — useful for diagnostics/telemetry. */
  source: 'cms' | 'fallback';
}

/* -------------------------------------------------------------------------- */
/* Client ID mapping                                                          */
/* -------------------------------------------------------------------------- */

const CLIENT_ID_MAP: Record<string, string> = {
  passkey: 'cvent_passkey',
};

/**
 * Maps the widget's internal client ID values (used in routes/URLs) to the
 * corresponding client ID keys published in the Content API / CMS database.
 *
 * For example:
 * - Code-level `'passkey'` is mapped to CMS-level `'cvent_passkey'`.
 */
const mapCodeClientIdToCmsClientId = (clientId: string): string => {
  return CLIENT_ID_MAP[clientId] ?? clientId;
};

/* -------------------------------------------------------------------------- */
/* Fallback resolvers                                                         */
/* -------------------------------------------------------------------------- */

/**
 * `content.json` has a rich, deeply-typed literal shape from
 * `resolveJsonModule`, so we access the relevant sub-trees via `unknown` casts
 * to keep the lookups type-safe without duplicating the literal type here.
 */
const contentUnsafe = content as unknown as {
  signIn?: {
    legalText?: Record<string, string>;
    message?: Record<string, string>;
  };
};

/** Resolve the disclaimer from bundled `content.json`. */
const fallbackDisclaimer = (clientId?: ClientId | string): string => {
  if (!clientId) return '';
  const legal = contentUnsafe.signIn?.legalText ?? {};
  const val = legal[clientId];
  return typeof val === 'string' ? val : '';
};

/** Resolve the auth-failed error from `KNOWN_ERROR_MESSAGES` (same rules as `getKnownErrorMessageByClientId`). */
const fallbackAuthFailed = (clientId?: ClientId | string): string => {
  const customMsg = clientId && KNOWN_ERROR_MESSAGES[`${clientId}_error`];
  if (typeof customMsg === 'string') {
    return customMsg;
  }
  return typeof KNOWN_ERROR_MESSAGES['default_error'] === 'string' ? KNOWN_ERROR_MESSAGES['default_error'] : '';
};

/** Resolve the locked-out error from bundled `content.json`. */
const fallbackLockedOut = (clientId?: ClientId | string): string => {
  const messages = contentUnsafe.signIn?.message ?? {};
  if (clientId && typeof messages[`locked_out_${clientId}`] === 'string') {
    return messages[`locked_out_${clientId}`];
  }
  return typeof messages['locked_out'] === 'string' ? messages['locked_out'] : '';
};

/* -------------------------------------------------------------------------- */
/* Public hook                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Returns the client-specific integration content (disclaimer + errors) for
 * the given `clientId`, sourced from CMS with fallback to bundled defaults.
 *
 * Consumers should still call `.replace('{REDIRECT_URI}', redirect_uri)` on
 * the error strings at render time — placeholder substitution is intentionally
 * left to the caller for parity with existing behavior.
 *
 * @param clientId The client integration identifier (e.g. `bet_mgm`,
 *                 `passkey`, `holiday_gift_shop`). May be `undefined` — in
 *                 which case the `default` entry is returned.
 */
export function useClientIntegrationContent(clientId?: ClientId | string): ClientIntegrationContent {
  const { data, loading } = useGetUIContent<UIIdentityContent>(UI_CONTENT_IDS.IDENTITY);

  return useMemo<ClientIntegrationContent>(() => {
    const integrations = data?.getUIContentById?.client_integrations;

    // Map widget route-level clientId to CMS database-level key (e.g. 'passkey' -> 'cvent_passkey')
    const mappedCmsId = clientId ? mapCodeClientIdToCmsClientId(clientId) : undefined;

    const cmsClient =
      mappedCmsId && Array.isArray(integrations)
        ? integrations.find(item => item?.client_id === mappedCmsId)
        : undefined;
    const defaults = Array.isArray(integrations) ? integrations.find(item => item?.client_id === 'default') : undefined;

    // Pick CMS value if present for this client, else CMS default, else bundled fallback.
    const rawDisclaimer = cmsClient?.disclaimer?.terms_and_conditions ?? defaults?.disclaimer?.terms_and_conditions;
    const rawAuthFailed = cmsClient?.errors?.authentication_failed ?? defaults?.errors?.authentication_failed;
    const rawLockedOut = cmsClient?.errors?.locked_out ?? defaults?.errors?.locked_out;

    // Runtime typeof guards for CMS content to avoid crashes if CMS payload contains non-string types.
    const cmsDisclaimer = typeof rawDisclaimer === 'string' ? rawDisclaimer : undefined;
    const cmsAuthFailed = typeof rawAuthFailed === 'string' ? rawAuthFailed : undefined;
    const cmsLockedOut = typeof rawLockedOut === 'string' ? rawLockedOut : undefined;

    const disclaimer = cmsDisclaimer ?? fallbackDisclaimer(clientId);
    const authFailedError = cmsAuthFailed ?? fallbackAuthFailed(clientId);
    const lockedOutError = cmsLockedOut ?? fallbackLockedOut(clientId);

    const source: 'cms' | 'fallback' =
      cmsDisclaimer !== undefined || cmsAuthFailed !== undefined || cmsLockedOut !== undefined ? 'cms' : 'fallback';

    return { disclaimer, authFailedError, lockedOutError, loading, source };
  }, [clientId, data, loading]);
}
